import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useTemplateContext, useVibesites } from '@vibesites/runtime/react';
import { EditableToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';
import { hyperlocalPageCopyFallback, resolveHyperlocalRichText } from './hyperlocalRichText';

export function HyperlocalPageCopy() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const { property } = useVibesites();
  const templateContext = useTemplateContext();
  const hyperlocal = templateContext?.hyperlocal as Record<string, unknown> | undefined;

  const rawHtml = useContentToken('hyperlocal_pagecopy.text');
  const schemaDefault = '<p>Page content will be displayed here.</p>';
  const pageCopy = hyperlocal?.page_copy as string | undefined;
  const htmlSource =
    rawHtml?.trim() === schemaDefault.trim() && pageCopy
      ? hyperlocalPageCopyFallback(pageCopy) ?? rawHtml
      : rawHtml ?? schemaDefault;

  const resolvedHtml = resolveHyperlocalRichText(htmlSource, property as Record<string, unknown>, hyperlocal);

  return (
    <section ref={sectionRef} className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="hyperlocal_pagecopy.text"
          type="richtext"
          label="Page Copy"
          defaultValue="<p>Page content will be displayed here.</p>"
          as="div"
          className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_h2]:font-heading [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-normal [&_h2]:text-[var(--vibe-color-text)] [&_h2]:mb-4 [&_h2]:mt-8 [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-normal [&_h3]:text-[var(--vibe-color-text)] [&_h3]:mb-3 [&_h3]:mt-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1"
        >
          <span dangerouslySetInnerHTML={{ __html: resolvedHtml }} style={{ display: 'contents' }} />
        </EditableToken>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalPageCopy',
  displayName: 'Hyperlocal Page Copy',
  tokens: {
    'hyperlocal_pagecopy.text': {
      label: 'Page Copy',
      type: 'richtext',
      defaultValue: '<p>Page content will be displayed here.</p>',
    },
  },
};

(HyperlocalPageCopy as any).contentSchema = contentSchema;

export default HyperlocalPageCopy;
